import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ControlContainer, FormControl, FormGroup } from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { CustomValidatorMessage, evaluate, LgdErrorStateMatcher, LgdFormControl, LgdFormControlNumberConfig, LgdNumberFormatType, } from "../lgdCommon";
import { LgdNumberConfig } from "./lgdNumberValueAccessor";
@Component({
    selector: "lgd-number",
    templateUrl: "./lgdNumber.tmpl.html",
    styleUrls: ["./lgdNumber.component.scss"],
    standalone: false
})
export class LgdNumberComponent implements OnInit, AfterViewInit, OnDestroy
{
    private readonly destructor$ = new Subject<void>();

    private _initialized: boolean;

    @ViewChild("input")
    private readonly inputControl: ElementRef<HTMLInputElement>;

    @Input()
    readonly controlName: string | number;

    @Input()
    readonly label: string;

    @Input()
    readonly hideLabel: boolean;

    @Input()
    readonly dense: boolean;

    readonly errorStateMatcher = new LgdErrorStateMatcher();

    constructor(private readonly controlContainer: ControlContainer) { }

    get initialized(): boolean
    {
        return this._initialized;
    }

    get formGroup(): FormGroup
    {
        return this.controlContainer.control as FormGroup;
    }

    get formControl(): FormControl<number | string>
    {
        if (!this.formGroup || (this.controlName !== undefined && this.controlName == null))
        {
            throw new Error("Cannot access either formGroup or controlName.");
        }

        const controlNameStr = typeof this.controlName === "string" ? this.controlName : `${this.controlName}`;
        return this.formGroup.get(controlNameStr) as FormControl<number | string>;
    }

    get lgdFormControl(): LgdFormControl<LgdFormControlNumberConfig>
    {
        return LgdFormControl.isLgdFormControl(this.formControl) ? this.formControl : undefined;
    }

    get max(): number
    {
        return this.lgdFormControl ? evaluate(this.lgdFormControl.data.max) : 0;
    }

    get min(): number
    {
        return this.lgdFormControl ? evaluate(this.lgdFormControl.data.min) : 0;
    }

    get precision(): number
    {
        return this.lgdFormControl ? evaluate(this.lgdFormControl.data.precision) : 0;
    }

    get scale(): number
    {
        return this.lgdFormControl ? evaluate(this.lgdFormControl.data.scale) : 0;
    }

    get filter(): LgdNumberFormatType
    {
        return this.lgdFormControl ? this.lgdFormControl.data.filter : "decimal";
    }

    get noCommas(): boolean
    {
        return this.lgdFormControl ? evaluate(this.lgdFormControl.data.noCommas) : false;
    }

    private _config: LgdNumberConfig;

    get config(): LgdNumberConfig
    {
        return (
            this._config ||
            (this._config = {
                precision: this.precision,
                scale: this.scale,
                filter: this.filter,
                noCommas: this.noCommas,
            })
        );
    }

    hasCustomValidators: boolean;

    customErrors: ReadonlyArray<CustomValidatorMessage>;

    ngOnInit(): void
    {
        this._initialized = true;
        if (this.lgdFormControl)
        {
            this.hasCustomValidators = !!this.lgdFormControl.data.customValidators;
            this.customErrors = this.hasCustomValidators
                ? this.lgdFormControl.data.customValidators.reduce(
                    (l, x) => l.concat(x.getErrorMessage(this.label)),
                    []
                )
                : [];
        }
    }

    ngAfterViewInit(): void
    {
        if (!this.inputControl)
        {
            console.warn(`LgdNumberComponent.ngAfterViewInit - ViewChild inputElement is not defined!`);
        }
        if (this.lgdFormControl)
        {
            this.lgdFormControl.onFocus
                .pipe(takeUntil(this.destructor$))
                .subscribe((shouldFocus: boolean) =>
                {
                    if (shouldFocus)
                    {
                        /**
                         * Calling in a setTimeout to avoid a bug in our current @angular version.
                         */
                        setTimeout(() =>
                        {
                            this.inputControl.nativeElement.focus();
                        });
                    }
                });
        }
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    hasError(id: string): boolean
    {
        return !!this.formControl && this.formControl.hasError(id);
    }

    /**
     * @deprecated - LgdFormControl now supports notifying form controls of focus commands. This method is no longer
     * necessary, and should be removed in the future.
     */
    focus(): void
    {
        this.inputControl.nativeElement.focus();
    }

    get isDense(): string
    {
        return this.dense ? "dense" : "";
    }

    get isInvalid(): boolean
    {
        return this.formControl.touched && this.formControl.invalid;
    }

    get isRequired(): boolean
    {
        return !!this.lgdFormControl && evaluate(this.lgdFormControl.data.required);
    }

    showErrorMessage(id: string): boolean
    {
        return this.formControl.touched && this.hasError(id);
    }

    get helperTextSpacer(): boolean
    {
        return this.formControl.valid;
    }
}
